"use client";

import { useContext, useEffect } from "react";
import { useRouter } from "next/navigation";

import { Button } from "@/components/Button";

import { useToast } from "@/hooks/ui/useToast";
import { useQR, useQRStyle } from "@/hooks/qr/useQR";

import { getTokenData } from "@/services/CookiesService";

import { ActionTypes } from "@/internals/constants/actionTypes";

import { omittedValues, getQRCodeUrl } from "@/utils/qrEditorHelper";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";
import { QR_PAGE_INDEX } from "@/utils/qrEditorNavigatorHelper";
import { paths } from "@/utils/paths";

import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";

import { setPendingActionCookie } from "@/actions/setPendingActionCookie";
import { getPendingActionCookie } from "@/actions/getPendingActionCookie";
import { clearPendingActionCookie } from "@/actions/clearPendingActionCookie";

import { PreviewToggleProps } from "./types";
import { OnTogglePreviewAction } from "@/types/pendingActions";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";

import "./styles.scss";

export const PreviewToggle = ({
  previewType,
  setPreviewType,
  isQRActivated,
  setQrRaw,
  previewButtonsTranslations,
}: PreviewToggleProps) => {
  const { toast } = useToast();

  const { getRawData, updateQRCode } = useQRStyle();
  const { requestQR } = useQR();
  const router = useRouter();

  const { setIsQrLoading, qrData, setQrData } =
    useContext<QREditorContextProps<any>>(QREditorContext);

  const getQRPreview = () => {
    const tokenData = getTokenData();
    if (tokenData?.isTokenExpired) {
      const currentPageIndex = qrData?.currentPageIndex;
      const qrType = qrData?.type;

      const isDesignPage = currentPageIndex === QR_PAGE_INDEX.DESIGN_QR ? "design" : undefined;

      const pendingAction: OnTogglePreviewAction = {
        type: ActionTypes.TOGGLE_PREVIEW,
        redirectTo: paths.qrEditor(qrType, isDesignPage),
      };
      setPendingActionCookie(pendingAction);

      router.push(paths.signup());
    } else {
      setIsQrLoading(false);
      setPreviewType("qr");
      updateQRCode({
        qrStyle: qrData.qrStyle,
        qrUrl: getQRCodeUrl(qrData, true),
      });
      if (requestQR) {
        setIsQrLoading(true);
        requestQR()
          .then(async (data: any) => {
            if (data !== undefined) {
              setQrData(
                {
                  ...qrData,
                  qrCodeId: data?.id || qrData.qrCodeId,
                  qrCodeReference: data?.reference || qrData.qrCodeReference,
                },
                [...omittedValues(qrData.data)]
              );
              const raw = await getRawData();
              setQrRaw && setQrRaw(raw);
              setIsQrLoading(false);
            }
          })
          .catch((e) => {
            const errorMessage = ApiErrorMessageBuilder.buildToastMessage(e, {
              showDetails: true,
              includeCode: true,
              includeTimestamp: true,
              fallbackMessage: API_ERROR_FALLBACKS.QR_EDITOR.PREVIEW,
            });
            toast({ message: errorMessage, type: "error", marginBottom: 80 });
          });
      }
    }
  };

  useEffect(() => {
    const parsedPendingAction = getPendingActionCookie();

    parsedPendingAction.then((pendingAction) => {
      if (pendingAction) {
        if (pendingAction.type === ActionTypes.TOGGLE_PREVIEW) {
          getQRPreview();
          clearPendingActionCookie();
        }
      }
    });
  }, []);

  return (
    <div className="qr-preview__header__buttons-toggle">
      <Button
        type="button"
        dataQA="qr-editor-preview-button"
        secondary
        onClick={() => setPreviewType("data")}
        className={previewType === "qr" ? "deactivated" : undefined}
        label={previewButtonsTranslations?.previewPageButtonLabel}
        customClass="button-toggle"
        square
      />
      <Button
        type="button"
        dataQA="qr-editor-qr-preview-button"
        secondary
        onClick={() => getQRPreview()}
        disabled={!isQRActivated}
        className={previewType === "data" ? "deactivated" : undefined}
        label={previewButtonsTranslations?.qrCodeButtonLabel}
        customClass="button-toggle"
        square
      />
    </div>
  );
};
